import { booleanAttribute } from "#ce/utils/props";

// `value` is absent on purpose: the element owns a live `value` property, which a declared prop
// would shadow with an accessor onto the prop store. The attribute seeds it and names the reset value.
export const props = Object.freeze({
  /**
   * Array of items the dropdown filters against. Each entry is either a plain string (used for both the title and the value) or an object whose `title`/`value` keys are read by default. Pass via `.prop` because arrays cannot be expressed as attribute strings.
   *
   * @type {Array<String|Object>}
   * @default []
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Key on each item that holds the visible label used for filtering and rendering.
   *
   * @type {String}
   * @default "title"
   */
  itemTitle: {
    type: String,
    default: "title",
  },
  /**
   * Current text inside the input. Bind this and listen for `update-search` to drive external (server-side) filtering. Leave unset to let the component manage its own search state.
   *
   * @type {String}
   * @default undefined
   */
  search: {
    type: String,
    default: undefined,
  },
  /**
   * Disable the built-in client-side filtering. Set this when the items array is already filtered by a server-side query keyed off `update-search`.
   *
   * @type {Boolean}
   * @default false
   */
  noFilter: {
    type: Boolean,
    default: false,
  },
  /**
   * Empty-state message shown inside the dropdown once the user has typed but no item matches.
   *
   * @type {String}
   * @default "No results found"
   */
  noDataText: {
    type: String,
    default: "No results found",
  },
  /**
   * Whether the control is disabled.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Require a selection before the form can be submitted. Presence-based, the way the platform
   * reads a boolean attribute: any value other than `false` constrains the field.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required field is submitted without a selection. Names what
   * this particular field is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
});
